/**
 * BestPracticeAnalyzerTab Component
 *
 * Provides continuous monitoring against industry frameworks including CISA SCuBA,
 * NIST SP 800-53, Microsoft MCSB, and EIDSCA. Features framework alignment cards,
 * violation tracking with severity and waiver system, and trend charts.
 *
 * Formerly "Standards Compliance" - rebranded to better communicate purpose.
 *
 * Part of the Security Center hub.
 */

import { useState, useEffect, useMemo, useCallback, forwardRef, useImperativeHandle } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Tab } from '@headlessui/react'
import toast from 'react-hot-toast'
import { useMsal } from '@azure/msal-react'
import PillarBrief from '@/components/PostureBrief/PillarBrief'
import PriorityQueue from '@/components/PostureBrief/PriorityQueue'
import { SectionEyebrow, SectionHeadline } from '@/components/PostureBrief/SectionHeadline'
import EditorialHeatmap, {
  type Intensity,
  type HeatmapRow,
} from '@/components/PostureBrief/EditorialHeatmap'
import ScoreDeltaChip from '@/components/Dashboard/v2/ScoreDeltaChip'
import { composeBpaBrief } from '@/lib/security-center/bpa-brief'
import {
  compute7dFrameworkDelta,
  frameworkSparkline,
} from '@/lib/security-center/framework-trend'
import { formatTriggeredBy } from '@/lib/userDisplay'
import { usePriorityActions } from '@/hooks/usePriorityActions'
import {
  Cog6ToothIcon,
  ArrowPathIcon,
  ChartBarIcon,
  ListBulletIcon,
  ClockIcon,
  ViewColumnsIcon,
  ExclamationTriangleIcon,
  FunnelIcon,
  KeyIcon,
  EnvelopeIcon,
  ChatBubbleLeftRightIcon,
  FolderIcon,
  ShieldCheckIcon,
  CubeIcon,
} from '@heroicons/react/24/outline'
import {
  BetaBadge,
  ViolationsTable,
  StandardsConfigModal,
  WaiverModal,
  ComplianceRadialScore,
  GroupedPoliciesDisplay,
  FrameworkControlsList,
  NistDisclaimer,
} from '@/components/Compliance'
import {
  useComplianceDashboard,
  useComplianceScoreHistory,
  useControlChanges,
  useTriggerEvaluation,
  useCreateWaiver,
  useUpdateWaiver,
  useDeleteWaiver,
  useBulkUpdateTenantStandards,
  useToggleIncludeInScore,
} from '@/hooks/useComplianceData'
import type {
  ComplianceViolation,
  ComplianceTrendPoint,
  ControlChange,
  CreateWaiverRequest,
  UpdateWaiverRequest,
  UpdateStandardConfigRequest,
  M365Workload,
  FrameworkScoreDisplay,
  ViolationStatus,
  WorkloadCollectionHealth,
} from '@/types'
import { M365_WORKLOADS, getWorkloadForProduct } from '@/types'

export interface BestPracticeAnalyzerTabRef {
  /** Refresh the current compliance data */
  refresh: () => void
  /** Trigger a new policy evaluation */
  triggerEvaluation: () => void
}

export interface BestPracticeAnalyzerTabProps {
  /** The tenant ID to analyze */
  tenantId: string | undefined
  /** Callback when the overall score changes */
  onScoreChange?: (score: number | null) => void
}

type InternalTabKey = 'by-standard' | 'by-workload' | 'violations' | 'history'

interface InternalTabItem {
  key: InternalTabKey
  name: string
  icon: typeof ChartBarIcon
}

const internalTabs: InternalTabItem[] = [
  { key: 'by-standard', name: 'By standard', icon: ViewColumnsIcon },
  { key: 'by-workload', name: 'By workload', icon: ListBulletIcon },
  { key: 'violations', name: 'Violations', icon: ListBulletIcon },
  { key: 'history', name: 'History', icon: ClockIcon },
]

/**
 * Preferred display ordering for frameworks across Overview and By Standard tabs.
 * MCSB first because it is always included in the Guardian Score.
 */
import { FRAMEWORK_ORDER } from '@/lib/frameworkUtils'
export { FRAMEWORK_ORDER }

/** Framework blurbs - concise descriptions for each standard heading */
export const FRAMEWORK_BLURBS: Record<string, string> = {
  'MCSB': 'Microsoft-maintained security baseline for cloud services covering identity, networking, data protection, and more.',
  'EIDSCA': 'Community-driven checks for Entra ID (Azure AD) security settings, conditional access, and authentication policies.',
  'NIST_800_53': 'Federal security controls framework mapping M365 configurations to NIST SP 800-53 Rev 5 requirements.',
  'SCUBA': 'CISA\'s Secure Cloud Business Applications project with prescriptive M365 security configuration baselines.',
  'CIS': 'CIS Microsoft 365 Foundations Benchmark v5.0.0 with prescriptive security configuration recommendations across all M365 services.',
}

import { refMatchesFramework } from '@/lib/frameworkUtils'

// Editorial micro-pill className shared by HealthBadge and the last-evaluated
// strip below. The tinted background + bold uppercase tracked label reads at
// home next to navy headings on white workload cards.
const EDITORIAL_PILL_BASE =
  'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.16em]'

// Workload icons and colors for display - using Heroicons for consistency
const WORKLOAD_DISPLAY: Record<M365Workload, {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>
  color: string
}> = {
  Identity: { icon: KeyIcon, color: '#3b82f6' },              // blue-500
  Exchange: { icon: EnvelopeIcon, color: '#f97316' },         // orange-500
  Teams: { icon: ChatBubbleLeftRightIcon, color: '#8b5cf6' }, // violet-500
  SharePoint: { icon: FolderIcon, color: '#14b8a6' },         // teal-500
  Defender: { icon: ShieldCheckIcon, color: '#ef4444' },      // red-500
  Other: { icon: CubeIcon, color: '#6b7280' },                // gray-500
}

/**
 * Health badge for workload data collection status.
 * Shows green when all EXPECTED data sources collected,
 * amber when partial, red when no expected data collected.
 * Respects graphExpected/powerShellExpected so workloads like Identity
 * (Graph-only) don't permanently show "Partial".
 */
function HealthBadge({ health }: { health: WorkloadCollectionHealth }) {
  const { graphCollected, powerShellCollected, graphExpected, powerShellExpected } = health.health
  // "Healthy" = all expected data sources were collected
  const allCollected = (graphCollected || !graphExpected) && (powerShellCollected || !powerShellExpected)
  // "No data" = none of the expected sources were collected
  const noneCollected = (graphExpected && !graphCollected) && (powerShellExpected ? !powerShellCollected : false)
  // For single-source workloads, check only the expected source
  const singleSourceNone = !allCollected && ((graphExpected && !graphCollected && !powerShellExpected) ||
                                              (powerShellExpected && !powerShellCollected && !graphExpected))

  // Severity dot kept on amber/red rows so the state is scannable without
  // relying on color alone. Healthy state drops the dot since the green tint
  // already reads as "all good" at a glance.
  if (allCollected) {
    return (
      <span className={`${EDITORIAL_PILL_BASE} bg-[#E2F1E8] text-[#1F6B43]`}>
        Data collected
      </span>
    )
  }

  if (noneCollected || singleSourceNone) {
    return (
      <span className={`${EDITORIAL_PILL_BASE} bg-[#FBE8E8] text-[#A03434]`}>
        <span className="h-1.5 w-1.5 rounded-full bg-[#C84B4B]" />
        No data collected
      </span>
    )
  }

  return (
    <span className={`${EDITORIAL_PILL_BASE} bg-[#FDF3DF] text-[#8C6B22]`}>
      <span className="h-1.5 w-1.5 rounded-full bg-[#C9AD80]" />
      Partial data
    </span>
  )
}

/* -------------------------------------------------------------------------- */
/* Phase B/C helpers — pure, exported only at module scope so they can be     */
/* unit-tested without mounting the component.                                 */
/* -------------------------------------------------------------------------- */

/**
 * Heatmap intensity ramp for SHALL counts. Even one open SHALL is critical
 * (red); two or more starts the saturated red treatment. Exported because
 * the BPA tab and any future workload heatmap should agree on the ramp.
 */
function intensityFromShall(n: number): Intensity {
  if (n === 0) return 'l0'
  if (n <= 2) return 'l3'
  return 'l4'
}

/** SHOULD ramp — one is amber-ish, three or more is firmly amber. */
function intensityFromShould(n: number): Intensity {
  if (n === 0) return 'l0'
  if (n <= 2) return 'l2'
  if (n <= 5) return 'l3'
  return 'l4'
}

/** MAY ramp — even high counts stay editorial (blue), never red. */
function intensityFromMay(n: number): Intensity {
  if (n === 0) return 'l0'
  if (n <= 5) return 'l1'
  return 'l2'
}

/**
 * Extract the framework code from a BPA priority-action id. Action ids are
 * `bpa:<policyCode>` where policyCode is `<framework>.<...>` (e.g.
 * `SCuBA.AAD.4.1v1`, `EIDSCA.PSO.01`, `MCSB.IM-1`). Returns the leading
 * framework code, or `null` if the id is malformed.
 */
function extractFrameworkCodeFromActionId(id: string): string | null {
  if (!id.startsWith('bpa:')) return null
  const rest = id.slice('bpa:'.length)
  const dot = rest.indexOf('.')
  if (dot <= 0) return null
  return rest.slice(0, dot)
}

/**
 * Trailing-clause verbs for the "Recent control moves" feed (§6.5). Keyed by
 * `ControlChange.changeKind`. Period-then-clause style, no em-dashes (CLAUDE.md
 * voice rules). Each value is rendered after the policy title, e.g.
 * "Conditional Access. Started failing."
 */
const CONTROL_CHANGE_TRAILING_CLAUSE: Record<ControlChange['changeKind'], string> = {
  Improved: 'Came into compliance.',
  Regressed: 'Started failing.',
  Waived: 'Was waived.',
  WaiverLapsed: 'Is failing again. Waiver lapsed.',
}

/**
 * Right-column renderer for a "Recent control moves" row (§6.5).
 *   * `Waived` always reads as a muted pill (positive magnitude but neutral
 *     display per plan §4).
 *   * Unweighted policies (`scoreDelta == null`) render "Not scored".
 *   * Everything else uses ScoreDeltaChip (which owns sign coloring and the
 *     0.05 noise threshold).
 */
function renderControlChangeRightSlot(change: ControlChange): React.ReactNode {
  if (change.changeKind === 'Waived') {
    return (
      <span className="text-[12px] italic text-[color:var(--color-mute,#6B7280)] whitespace-nowrap">
        Waived
      </span>
    )
  }
  if (change.scoreDelta == null) {
    return (
      <span className="text-[12px] italic text-[color:var(--color-mute,#6B7280)] whitespace-nowrap">
        Not scored
      </span>
    )
  }
  return <ScoreDeltaChip delta={change.scoreDelta} />
}

/* -------------------------------------------------------------------------- */
/* Phase B/C visual sub-components — kept inside this module since they're    */
/* tightly coupled to the BPA tab's editorial palette.                         */
/* -------------------------------------------------------------------------- */

/** Inline-SVG sparkline for the framework cards (§6.2). Draws a smooth
 *  polyline over `series` (0-100 scores). Renders nothing when fewer than
 *  two points are available so empty cards don't carry phantom strokes. */
function FrameworkSparkline({
  series,
  width = 96,
  height = 28,
}: {
  series: number[]
  width?: number
  height?: number
}) {
  if (series.length < 2) {
    return (
      <span className="text-[10px] italic text-[color:var(--color-faint,#9CA3AF)]">
        — 14d
      </span>
    )
  }
  // Map score (0-100) → y (height-pad to pad). We pin the y-axis to a fixed
  // 0-100 range rather than auto-scaling so users can compare cards visually.
  const pad = 2
  const xStep = (width - pad * 2) / (series.length - 1)
  const points = series
    .map((v, i) => {
      const x = pad + i * xStep
      const y = height - pad - ((Math.max(0, Math.min(100, v)) / 100) * (height - pad * 2))
      return `${x.toFixed(1)},${y.toFixed(1)}`
    })
    .join(' ')
  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      aria-hidden
      className="overflow-visible"
    >
      <polyline
        points={points}
        fill="none"
        stroke="var(--tone-accent-deep, #4F86A8)"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/** Inline-SVG line+area chart for the 30-day composite trend (§6.4). No
 *  Recharts dependency: we draw a polyline + filled area + axis ticks
 *  manually. Width is responsive via viewBox. */
function CompositeTrendChart({
  points,
  width = 720,
  height = 200,
}: {
  points: ComplianceTrendPoint[]
  width?: number
  height?: number
}) {
  if (points.length < 2) {
    return (
      <div className="rounded-[4px] border border-dashed border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper,#fff)] px-6 py-8 text-center text-[13px] italic text-[color:var(--color-mute,#6B7280)]">
        Two or more daily snapshots are needed to chart a trend.
      </div>
    )
  }
  const padL = 36
  const padR = 12
  const padT = 16
  const padB = 28
  const innerW = width - padL - padR
  const innerH = height - padT - padB

  const scores = points.map(p => p.overallScore)
  const min = Math.max(0, Math.floor(Math.min(...scores) - 4))
  const max = Math.min(100, Math.ceil(Math.max(...scores) + 4))
  const range = Math.max(1, max - min)

  const xAt = (i: number) =>
    padL + (points.length === 1 ? innerW / 2 : (i / (points.length - 1)) * innerW)
  const yAt = (v: number) => padT + innerH - ((v - min) / range) * innerH

  const linePoints = points.map((p, i) => `${xAt(i).toFixed(1)},${yAt(p.overallScore).toFixed(1)}`).join(' ')
  const areaPath =
    `M ${xAt(0).toFixed(1)},${(padT + innerH).toFixed(1)} ` +
    points.map((p, i) => `L ${xAt(i).toFixed(1)},${yAt(p.overallScore).toFixed(1)}`).join(' ') +
    ` L ${xAt(points.length - 1).toFixed(1)},${(padT + innerH).toFixed(1)} Z`

  // Y-axis ticks at min, mid, max. Date ticks at first / mid / last point.
  const yTicks = [min, Math.round((min + max) / 2), max]
  const dateTickIdxs = [0, Math.floor((points.length - 1) / 2), points.length - 1]
  const fmtDate = (s: string) =>
    new Date(s).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className="w-full"
      role="img"
      aria-label="30-day Best Practice score trend"
      preserveAspectRatio="none"
    >
      {/* Y-axis grid + labels */}
      {yTicks.map(t => (
        <g key={t}>
          <line
            x1={padL}
            x2={width - padR}
            y1={yAt(t)}
            y2={yAt(t)}
            stroke="var(--color-rule, #E6E1D5)"
            strokeDasharray="2 3"
            strokeWidth={0.6}
          />
          <text
            x={padL - 6}
            y={yAt(t) + 3}
            textAnchor="end"
            fontSize={9}
            fill="var(--color-faint, #9CA3AF)"
          >
            {t}
          </text>
        </g>
      ))}

      {/* Filled area */}
      <path d={areaPath} fill="var(--tone-accent-soft, rgba(79,134,168,0.18))" />

      {/* Trend line */}
      <polyline
        points={linePoints}
        fill="none"
        stroke="var(--tone-accent-deep, #3D7393)"
        strokeWidth={1.6}
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Date ticks */}
      {dateTickIdxs.map(i => (
        <text
          key={i}
          x={xAt(i)}
          y={height - 8}
          textAnchor={i === 0 ? 'start' : i === points.length - 1 ? 'end' : 'middle'}
          fontSize={9}
          fill="var(--color-faint, #9CA3AF)"
        >
          {fmtDate(points[i].date)}
        </text>
      ))}
    </svg>
  )
}

/** Framework card with sparkline (§6.2). Editorial palette only — no
 *  `bg-dark-*`. The ★ toggle wires straight to the existing
 *  `useToggleIncludeInScore` mutation via the parent's onToggleStar prop.
 *
 *  Starred cards (those contributing to the Guardian Score) carry a 4px
 *  double-navy top border — the same section-break signature used between
 *  editorial sections. Unstarred-but-evaluated cards keep the plain 1px
 *  rule border so the starred set reads as the dominant tier at a glance. */
function FrameworkSparklineCard({
  framework,
  delta7d,
  sparkline,
  onToggleStar,
  onOpen,
}: {
  framework: FrameworkScoreDisplay
  delta7d: number | null
  sparkline: number[]
  onToggleStar: () => void
  onOpen: () => void
}) {
  const isAlwaysIncluded = (framework.code ?? '').toUpperCase() === 'MCSB'
  const starred = framework.includeInGuardianScore || isAlwaysIncluded
  const score = Math.round(framework.score ?? 0)
  return (
    <article
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onOpen()
        }
      }}
      aria-label={`Open ${framework.name} in Section 06`}
      className={[
        'flex cursor-pointer flex-col gap-3 rounded-[6px] border border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper,#fff)] transition-colors hover:border-[color:var(--color-navy,#212938)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-navy,#212938)] focus-visible:ring-offset-2',
        // Starred cards: shave 3px from top padding so the 4px double-rule
        // doesn't push interior content down vs. an unstarred neighbor.
        starred ? 'px-5 pb-5 pt-[17px]' : 'p-5',
      ].join(' ')}
      style={starred ? { borderTop: '4px double var(--color-navy, #212938)' } : undefined}
    >
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--color-faint,#9CA3AF)]">
            {framework.code}
          </div>
          <h3 className="mt-1 truncate text-[15px] font-semibold text-[color:var(--color-navy,#212938)]">
            {framework.name}
          </h3>
        </div>
        <button
          type="button"
          onClick={e => {
            e.stopPropagation()
            onToggleStar()
          }}
          disabled={isAlwaysIncluded}
          aria-label={
            isAlwaysIncluded
              ? `${framework.code} is always included in the Guardian Score`
              : starred
                ? `Remove ${framework.code} from Guardian Score`
                : `Include ${framework.code} in Guardian Score`
          }
          title={
            isAlwaysIncluded
              ? 'MCSB is always included in your Guardian Score'
              : starred
                ? 'Click to remove from Guardian Score'
                : 'Click to include in Guardian Score'
          }
          className={[
            'flex-none text-[18px] leading-none transition-colors',
            isAlwaysIncluded ? 'cursor-not-allowed opacity-90' : 'hover:scale-110',
            starred
              ? 'text-[color:var(--tone-accent-deep,#3D7393)]'
              : 'text-[color:var(--color-faint,#9CA3AF)]',
          ].join(' ')}
        >
          {starred ? '★' : '☆'}
        </button>
      </header>

      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="font-display text-[36px] font-extrabold leading-none tabular-nums tracking-[-0.02em] text-[color:var(--color-navy,#212938)]">
            {score}
            <span className="ml-1 text-[14px] font-semibold text-[color:var(--color-mute,#6B7280)]">
              %
            </span>
          </div>
          <div className="mt-1 text-[11px] uppercase tracking-[0.12em] text-[color:var(--color-mute,#6B7280)]">
            {framework.passedControls}/{framework.totalControls} passing
          </div>
        </div>
        <FrameworkSparkline series={sparkline} />
      </div>

      <footer className="flex items-center justify-between gap-3 pt-1">
        <span
          className={[
            'text-[11px] font-semibold tabular-nums',
            delta7d == null
              ? 'text-[color:var(--color-faint,#9CA3AF)]'
              : delta7d > 0
                ? 'text-[#3F8259]'
                : delta7d < 0
                  ? 'text-[#A03434]'
                  : 'text-[color:var(--color-mute,#6B7280)]',
          ].join(' ')}
        >
          {delta7d == null
            ? '— 7d'
            : `${delta7d > 0 ? '▲ +' : delta7d < 0 ? '▼ ' : '·'}${Math.abs(delta7d)} 7d`}
        </span>
        <button
          type="button"
          onClick={e => {
            e.stopPropagation()
            onOpen()
          }}
          className="text-[11px] font-bold uppercase tracking-[0.16em] text-[color:var(--color-navy,#212938)] hover:underline"
        >
          Open →
        </button>
      </footer>
    </article>
  )
}

/** Recessive "disabled" card — shown for every framework that is NOT currently
 *  contributing to the Guardian Score (whether already evaluated or not).
 *  Replaces both the empty-star variant of FrameworkSparklineCard and the
 *  previous dashed "Available" card. Score, sparkline, delta, and "Open →"
 *  are intentionally hidden; backend evaluation keeps running in the
 *  background so the reveal on Enable is instant.
 *
 *  This component does NOT carry the double-rule cap — that signature is
 *  reserved for starred cards so the eye lands on the in-score tier first. */
function DisabledFrameworkCard({
  code,
  name,
  onEnable,
  isPending = false,
}: {
  code: string
  name: string
  onEnable: () => void
  isPending?: boolean
}) {
  return (
    <article className="flex flex-col gap-3 rounded-[6px] border border-[color:var(--color-line,#E6E8EC)] bg-[color:var(--color-cream,#FBF8F2)]/70 p-5">
      <header>
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--color-faint,#9CA3AF)]">
          {code}
        </div>
        <h3 className="mt-1 truncate text-[15px] font-semibold text-[color:var(--color-mute,#6B7280)]">
          {name}
        </h3>
      </header>
      <p className="text-[13px] italic leading-snug text-[color:var(--color-mute,#6B7280)]">
        Enable this framework to drive your score.
      </p>
      <div className="mt-auto flex items-center justify-between gap-3 pt-1">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onEnable()
          }}
          disabled={isPending}
          className="rounded-[2px] border border-[color:var(--color-navy,#212938)] bg-[color:var(--color-navy,#212938)] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-[color:var(--color-paper,#fff)] transition-opacity hover:opacity-85 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-navy,#212938)] focus-visible:ring-offset-2 disabled:cursor-wait disabled:opacity-60"
        >
          {isPending ? 'Enabling…' : 'Enable'}
        </button>
        <span className="text-[10px] uppercase tracking-[0.12em] text-[color:var(--color-faint,#9CA3AF)]">
          Hidden from score
        </span>
      </div>
    </article>
  )
}

/** Compact KPI tile under the 30-day trend chart. */
function TrendKpi({
  label,
  value,
  tone = 'neutral',
}: {
  label: string
  value: string
  tone?: 'pos' | 'neg' | 'neutral'
}) {
  const valueColor =
    tone === 'pos'
      ? '#3F8259'
      : tone === 'neg'
        ? '#A03434'
        : 'var(--color-navy, #212938)'
  return (
    <div className="rounded-[4px] border border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper,#fff)] px-4 py-3">
      <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-[color:var(--color-faint,#9CA3AF)]">
        {label}
      </div>
      <div
        className="font-display mt-1 text-[22px] font-extrabold tabular-nums leading-none"
        style={{ color: valueColor }}
      >
        {value}
      </div>
    </div>
  )
}

/** Format a signed delta as "+3.2" / "-1.4" / "0". */
function formatSignedDelta(n: number): string {
  if (n === 0) return '0'
  return n > 0 ? `+${n}` : `${n}`
}

/**
 * BestPracticeAnalyzerTab - M365 security configuration checks based on industry frameworks.
 *
 * Evaluates tenant against checks derived from:
 * - CISA SCuBA: Secure Cloud Business Applications
 * - NIST SP 800-53: Security and Privacy Controls
 * - Microsoft MCSB: Microsoft Cloud Security Benchmark
 * - EIDSCA: Entra ID Security Configuration Analyzer
 *
 * Note: These are built-in checks for common security configurations. For
 * comprehensive compliance assessment, pair with Microsoft Purview Compliance Manager.
 */
const BestPracticeAnalyzerTab = forwardRef<
  BestPracticeAnalyzerTabRef,
  BestPracticeAnalyzerTabProps
>(function BestPracticeAnalyzerTab({ tenantId, onScoreChange }, ref) {
  // Modal state
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false)
  const [isWaiverModalOpen, setIsWaiverModalOpen] = useState(false)
  const [selectedViolation, setSelectedViolation] = useState<ComplianceViolation | null>(null)

  // Internal tab index (controlled so we can switch programmatically)
  const [activeInternalTab, setActiveInternalTab] = useState(0)

  // Workload filter state for violations tab
  const [selectedWorkloadFilter, setSelectedWorkloadFilter] = useState<M365Workload | 'all'>('all')

  // Per-framework view mode for By Standard tab: 'issues' (default) or 'controls'
  const [frameworkViewMode, setFrameworkViewMode] = useState<Record<string, 'issues' | 'controls'>>({})

  // standardId of the framework whose Enable click is currently in flight.
  // Drives the per-card "Enabling…" state on DisabledFrameworkCard so the
  // pending indicator is scoped to the card the user clicked, not every
  // disabled card on the page.
  const [enablingStandardId, setEnablingStandardId] = useState<string | null>(null)

  // Phase B §6.1 — Top-six priority queue framework filter chip state.
  // Pure client-side filter on action.id (which carries the policy code, e.g.
  // `bpa:SCuBA.AAD.4.1v1`). Default = "all" (cross-framework view).
  const [bpaFrameworkFilter, setBpaFrameworkFilter] = useState<string>('all')

  // Top-six → Violations focus handoff. Cleared by ViolationsTable via onFocusConsumed
  // after the scroll fires, which lets a second click on the same row re-trigger.
  const [focusViolationCode, setFocusViolationCode] = useState<string | null>(null)
  const handleFocusConsumed = useCallback(() => setFocusViolationCode(null), [])

  // External entry handoff: Command Center pillar tiles and the "What to do
  // this week" queue deep-link with `?policy=<code>`. The Operations heatmap
  // also deep-links with `?workload=<wl>` to land here filtered by workload.
  // On mount we switch to the Violations sub-tab, apply the matching focus
  // (policy row) or filter (workload), and strip the param so a refresh
  // doesn't re-trigger. Wrapping the URL read in useSearchParams keeps it
  // reactive for in-page param changes (e.g. user back/forward).
  const [searchParams, setSearchParams] = useSearchParams()
  useEffect(() => {
    const policyParam = searchParams.get('policy')
    const rawWorkloadParam = searchParams.get('workload')
    const workloadParam = M365_WORKLOADS.find(w => w === rawWorkloadParam) ?? null
    if (!policyParam && !workloadParam) return
    const violationsTabIndex = internalTabs.findIndex(t => t.key === 'violations')
    if (violationsTabIndex >= 0) {
      setActiveInternalTab(violationsTabIndex)
    }
    if (policyParam) {
      setSelectedWorkloadFilter('all')
      setFocusViolationCode(policyParam)
    } else if (workloadParam) {
      // 'Other' isn't a chip on the Violations sub-tab — fall back to 'all'
      // rather than silently filter to nothing.
      setSelectedWorkloadFilter(workloadParam === 'Other' ? 'all' : workloadParam)
    }
    // Drop just the consumed params; preserve `tab=best-practices` and any
    // other unrelated query state.
    setSearchParams(
      prev => {
        prev.delete('policy')
        prev.delete('workload')
        return prev
      },
      { replace: true },
    )
  }, [searchParams, setSearchParams])

  // Section 03 heatmap → By Workload deep-dive handoff. `tick` increments on
  // every click so re-clicks remount the targeted GroupedPoliciesDisplay and
  // re-open the requested severity bucket even after a manual collapse.
  const [openWorkloadGroup, setOpenWorkloadGroup] = useState<{
    workload: M365Workload | null
    group: 'critical' | 'high' | 'medium' | null
    tick: number
  }>({ workload: null, group: null, tick: 0 })

  // Section 03 WAIVED column → Violations sub-tab handoff. Bumping `tick`
  // remounts ViolationsTable with the requested status filter applied,
  // mirroring the openWorkloadGroup pattern above.
  const [violationsTableSignal, setViolationsTableSignal] = useState<{
    status: ViolationStatus | 'all'
    tick: number
  }>({ status: 'all', tick: 0 })

  // React Query hooks for data fetching - using the dashboard hook
  const {
    dashboardData,
    collectionHealth,
    summary,
    violations,
    assignments,
    trendData: trends,
    isLoading,
    isError,
    refetch: refetchAll,
  } = useComplianceDashboard(tenantId)

  // 30-day score history feeds the framework sparklines (§6.2 — sliced to the
  // last 14 days) and the composite trend chart (§6.4). One fetch, one cache
  // entry — derived slices live in useMemo below.
  const { data: scoreHistory30d } = useComplianceScoreHistory(tenantId, 30)

  // §6.5 — Per-control flip feed (replaces the per-eval rollup; see
  // per-control-change-feed.md). Each row is one policy that transitioned
  // Pass↔Fail or was waived since the prior eval, with the signed GS delta.
  const {
    data: controlChanges,
    isError: controlChangesError,
    refetch: refetchControlChanges,
  } = useControlChanges(tenantId, 20)

  // Phase B §6.1 — Server-ranked top-6 BPA priority actions (curated SHALL
  // violations across starred frameworks). The hook takes the pillar filter
  // server-side; framework-level filtering happens client-side via
  // `bpaFrameworkFilter` so chips don't refetch.
  const { actions: bpaPriorityActions } = usePriorityActions({
    tenantId,
    maxCount: 6,
    pillar: 'bpa',
  })

  // Mutations
  const triggerEvaluationMutation = useTriggerEvaluation()
  const createWaiverMutation = useCreateWaiver()
  const updateWaiverMutation = useUpdateWaiver()
  const deleteWaiverMutation = useDeleteWaiver()
  const bulkUpdateStandardsMutation = useBulkUpdateTenantStandards()
  const toggleIncludeInScoreMutation = useToggleIncludeInScore()

  // Notify parent when score changes
  useEffect(() => {
    if (onScoreChange) {
      onScoreChange(summary?.overallScore ?? null)
    }
  }, [summary?.overallScore, onScoreChange])

  // Editorial brief — composer-driven standfirst at the top of the page.
  const { accounts } = useMsal()
  const userEmail = accounts[0]?.username
  const brief = useMemo(() => {
    const frameworks = (dashboardData?.frameworks ?? []).map((f: FrameworkScoreDisplay) => ({
      code: f.code,
      score: f.score ?? 0,
      starred: f.includeInGuardianScore,
      isAlwaysIncluded: (f.code ?? '').toUpperCase() === 'MCSB',
    }))

    const starred = frameworks.filter(f => f.starred || f.isAlwaysIncluded)
    const weakestFramework = starred.length > 0
      ? starred.reduce((min, f) => (f.score < min.score ? f : min))
      : undefined

    const openShallCount = violations.filter(
      v => v.status === 'Open' && v.criticality === 'SHALL',
    ).length

    // Source the #1 action from the same server-ranked queue that powers the
    // "Top six worth fixing first" table below. Picking the first SHALL out
    // of `violations` would land on whatever order the API returns and cause
    // the brief to disagree with the queue.
    const topRanked = bpaPriorityActions[0]
    const topAction = topRanked
      ? {
          title: topRanked.title,
          framework: extractFrameworkCodeFromActionId(topRanked.id) ?? undefined,
        }
      : undefined

    return composeBpaBrief({
      userEmail,
      tenantId,
      bpaScore: summary?.overallScore ?? null,
      frameworks,
      openShallCount,
      topAction,
      weakestFramework: weakestFramework
        ? { code: weakestFramework.code, score: weakestFramework.score }
        : undefined,
      delta30d: null,
    })
  }, [summary?.overallScore, dashboardData?.frameworks, violations, bpaPriorityActions, userEmail, tenantId])

  // Phase B §6.1 — Filter chips for the top-six priority queue. We derive the
  // available framework codes from the actions themselves (so chips reflect
  // what's actually in the queue) plus the "All" option. Each action's id is
  // shaped `bpa:<policyCode>` (e.g. `bpa:SCuBA.AAD.4.1v1`); the framework
  // segment is the first dotted token after the prefix.
  const availableBpaFrameworkChips = useMemo(() => {
    const codes = new Set<string>()
    for (const action of bpaPriorityActions) {
      const code = extractFrameworkCodeFromActionId(action.id)
      if (code) codes.add(code)
    }
    // Stable display order: prefer the FRAMEWORK_ORDER from frameworkUtils so
    // chip ordering matches the rest of the page; unknowns sort last.
    const ordered = Array.from(codes).sort((a, b) => {
      const oa = FRAMEWORK_ORDER[a] ?? 99
      const ob = FRAMEWORK_ORDER[b] ?? 99
      if (oa !== ob) return oa - ob
      return a.localeCompare(b)
    })
    return ['all', ...ordered]
  }, [bpaPriorityActions])

  // Apply the chip filter to the server-ranked actions. Filtering on `id` is
  // the only honest signal — `meta` carries the product, not the framework.
  const filteredBpaPriorityActions = useMemo(() => {
    if (bpaFrameworkFilter === 'all') return bpaPriorityActions
    return bpaPriorityActions.filter(a => {
      const code = extractFrameworkCodeFromActionId(a.id)
      return code != null && code.toUpperCase() === bpaFrameworkFilter.toUpperCase()
    })
  }, [bpaPriorityActions, bpaFrameworkFilter])

  // Phase B/C §6.2 — Framework cards with sparkline. Each card carries
  // current score, ★ inclusion, 7d delta, 14d sparkline series, and
  // pass/total counts. The 14d slice is derived from the 30d query above.
  const frameworkCardData = useMemo(() => {
    const fws = dashboardData?.frameworks ?? []
    const history14d = (scoreHistory30d ?? [])
      .slice()
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .slice(-14)
    return fws
      .filter(f => !f.isGuardian)
      .sort((a, b) => {
        const oa = FRAMEWORK_ORDER[a.code] ?? 99
        const ob = FRAMEWORK_ORDER[b.code] ?? 99
        return oa - ob
      })
      .map(f => ({
        framework: f,
        delta7d: compute7dFrameworkDelta(history14d, f.code, f.score ?? 0),
        sparkline: frameworkSparkline(history14d, f.code),
      }))
  }, [dashboardData?.frameworks, scoreHistory30d])

  // "Available · not yet starred" empty card slot — frameworks present in
  // assignments but not yet resolved on `dashboardData.frameworks`. Same
  // logic as <FrameworkRecommendation>.
  const unstarredAvailableFrameworks = useMemo(() => {
    const knownCodes = new Set(
      (dashboardData?.frameworks ?? []).map(f => (f.code ?? '').toUpperCase()),
    )
    return assignments
      .filter(a => !knownCodes.has((a.standardCode ?? '').toUpperCase()))
      .map(a => ({ code: a.standardCode, name: a.standardName, standardId: a.standardId }))
  }, [assignments, dashboardData?.frameworks])

  // Per-workload waived count. Heatmap surfaces this in the 4th column;
  // data-unavailable folds into the row sublabel instead.
  const waivedCountByWorkload = useMemo(() => {
    const counts: Record<M365Workload, number> = {
      Identity: 0,
      Exchange: 0,
      Teams: 0,
      SharePoint: 0,
      Defender: 0,
      Other: 0,
    }
    for (const v of violations) {
      if (v.status !== 'Waived') continue
      const w = getWorkloadForProduct(v.product || v.category)
      counts[w] += 1
    }
    return counts
  }, [violations])

  // Phase C §6.3 — Workload heatmap rows.
  //
  // Counts are bucketed client-side from the `violations` list, which the
  // dashboard hook already constrains to starred frameworks (the violations
  // endpoint defaults to onlyStarred=true on the server — see
  // StandardsComplianceController.cs:602). This keeps the heatmap scoped to
  // frameworks contributing to the Guardian Score, matching the score box,
  // pillar subtitle, and the Section 02 cards.
  //
  // We intentionally don't use `dashboardData.workloadScores` here even when
  // available: that server aggregation includes every assigned framework
  // regardless of star state, so its openShall/openShould/openMay counts
  // and per-workload health scores would over-report relative to the rest of
  // the page. Bringing back the Health column requires a server-side
  // `?onlyStarred=true` on the scores endpoint — tracked as a follow-up.
  const workloadHeatmapRows = useMemo<HeatmapRow[]>(() => {
    const buckets: Record<M365Workload, { shall: number; should: number; may: number; total: number }> = {
      Identity: { shall: 0, should: 0, may: 0, total: 0 },
      Exchange: { shall: 0, should: 0, may: 0, total: 0 },
      Teams: { shall: 0, should: 0, may: 0, total: 0 },
      SharePoint: { shall: 0, should: 0, may: 0, total: 0 },
      Defender: { shall: 0, should: 0, may: 0, total: 0 },
      Other: { shall: 0, should: 0, may: 0, total: 0 },
    }
    for (const v of violations) {
      if (v.status !== 'Open') continue
      const w = getWorkloadForProduct(v.product || v.category)
      const bucket = buckets[w]
      bucket.total += 1
      if (v.criticality === 'SHALL') bucket.shall += 1
      else if (v.criticality === 'SHOULD') bucket.should += 1
      else bucket.may += 1
    }
    return (Object.keys(buckets) as M365Workload[])
      .filter(w => w !== 'Other' || buckets[w].total > 0)
      .map(w => {
        const b = buckets[w]
        const waived = waivedCountByWorkload[w]
        return {
          label: w,
          sublabel: `${b.total} open`,
          cells: [
            { value: b.shall, intensity: intensityFromShall(b.shall), crit: b.shall > 0 },
            { value: b.should, intensity: intensityFromShould(b.should) },
            { value: b.may, intensity: intensityFromMay(b.may) },
            { value: waived, intensity: 'l0' as Intensity },
          ],
        }
      })
  }, [violations, waivedCountByWorkload])

  // Phase B §6.4 — 30-day composite trend KPIs. Computed server-side data
  // would be cleaner, but the trend array is already on the client and the
  // math is trivial.
  const trend30dKpis = useMemo(() => {
    const points = scoreHistory30d ?? []
    if (points.length === 0) return null
    const sorted = [...points].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
    )
    const scores = sorted.map(p => p.overallScore)
    const first = scores[0]
    const last = scores[scores.length - 1]
    return {
      points: sorted,
      net: Math.round((last - first) * 10) / 10,
      best: Math.round(Math.max(...scores) * 10) / 10,
      worst: Math.round(Math.min(...scores) * 10) / 10,
      avg: Math.round((scores.reduce((s, n) => s + n, 0) / scores.length) * 10) / 10,
    }
  }, [scoreHistory30d])

  // Handle refresh / re-evaluate
  const handleRefresh = async () => {
    if (!tenantId || triggerEvaluationMutation.isPending) return

    try {
      const response = await triggerEvaluationMutation.mutateAsync({
        tenantId,
        options: { forceRefresh: true },
      })

      // Check response status and show appropriate message
      if (response.status === 'NoStandards') {
        toast.error('No standards are enabled for this tenant. Enable standards in the Settings tab first.')
      } else if (response.status === 'Failed') {
        toast.error(response.message || 'Evaluation failed. Please try again.')
      } else if (response.status === 'Completed') {
        toast.success(response.message || 'Compliance evaluation completed successfully')
      } else {
        toast.success('Compliance evaluation completed successfully')
      }
    } catch (error) {
      console.error('Failed to trigger evaluation:', error)
      toast.error('Failed to run evaluation. Please try again.')
    }
  }

  // Expose methods to parent via ref
  useImperativeHandle(
    ref,
    () => ({
      refresh: () => {
        refetchAll()
        refetchControlChanges()
      },
      triggerEvaluation: () => {
        handleRefresh()
      },
    }),
    [refetchAll, refetchControlChanges, tenantId, triggerEvaluationMutation.isPending]
  )

  // Handle waiver creation
  const handleAddWaiver = (violation: ComplianceViolation) => {
    setSelectedViolation(violation)
    setIsWaiverModalOpen(true)
  }

  // Handle waiver edit
  const handleEditWaiver = (violation: ComplianceViolation) => {
    setSelectedViolation(violation)
    setIsWaiverModalOpen(true)
  }

  const handleSubmitWaiver = async (request: CreateWaiverRequest) => {
    if (!tenantId) return

    try {
      await createWaiverMutation.mutateAsync({
        tenantId,
        data: request,
      })
      setIsWaiverModalOpen(false)
      setSelectedViolation(null)
      toast.success('Waiver created successfully')
    } catch (error) {
      console.error('Failed to create waiver:', error)
      toast.error('Failed to create waiver. Please try again.')
    }
  }

  const handleUpdateWaiver = async (waiverId: string, request: UpdateWaiverRequest) => {
    if (!tenantId) return

    try {
      await updateWaiverMutation.mutateAsync({
        tenantId,
        waiverId,
        data: request,
      })
      setIsWaiverModalOpen(false)
      setSelectedViolation(null)
      toast.success('Waiver updated successfully')
    } catch (error) {
      console.error('Failed to update waiver:', error)
      toast.error('Failed to update waiver. Please try again.')
    }
  }

  const handleDeleteWaiver = async (violation: ComplianceViolation) => {
    if (!tenantId || !violation.waiver) return

    if (!window.confirm('Are you sure you want to remove this waiver? The policy will return to Open status and affect your compliance score.')) return

    try {
      await deleteWaiverMutation.mutateAsync({
        tenantId,
        waiverId: violation.waiver.id,
      })
      toast.success('Waiver removed successfully')
    } catch (error) {
      console.error('Failed to delete waiver:', error)
      toast.error('Failed to remove waiver. Please try again.')
    }
  }

  // Handle standards config save
  const handleSaveConfig = async (updates: UpdateStandardConfigRequest[]) => {
    if (!tenantId) return

    try {
      await bulkUpdateStandardsMutation.mutateAsync({
        tenantId,
        updates,
      })
      setIsConfigModalOpen(false)
      toast.success('Standards configuration updated successfully')
    } catch (error) {
      console.error('Failed to update standards config:', error)
      toast.error('Failed to update standards configuration. Please try again.')
    }
  }

  // Handle framework star toggle (include in Guardian score)
  const handleToggleIncludeInScore = async (framework: FrameworkScoreDisplay) => {
    if (!tenantId) return

    try {
      await toggleIncludeInScoreMutation.mutateAsync({
        tenantId,
        standardId: framework.standardId,
        includeInGuardianScore: !framework.includeInGuardianScore,
      })
      // Optimistic update already handled by the mutation hook
    } catch (error) {
      console.error('Failed to toggle include in score:', error)
      toast.error('Failed to update framework setting. Please try again.')
    }
  }

  // One-shot "Enable" for the DisabledFrameworkCard / disabled deep-dive row.
  // Always sets IncludeInGuardianScore=true (the recessive UI never disables —
  // unstarring still happens via the ★ button on the full FrameworkSparklineCard).
  // Tracks the in-flight standardId locally so only the clicked card shows
  // "Enabling…" instead of every disabled card on the page.
  const handleEnableFramework = async (standardId: string) => {
    if (!tenantId) return
    setEnablingStandardId(standardId)
    try {
      await toggleIncludeInScoreMutation.mutateAsync({
        tenantId,
        standardId,
        includeInGuardianScore: true,
      })
    } catch (error) {
      console.error('Failed to enable framework:', error)
      toast.error('Failed to enable framework. Please try again.')
    } finally {
      setEnablingStandardId(null)
    }
  }

  // Handle framework details view - switch to By Standard tab and scroll to the framework
  const handleViewFrameworkDetails = useCallback((framework: FrameworkScoreDisplay) => {
    const byStandardIndex = internalTabs.findIndex(t => t.key === 'by-standard')
    setActiveInternalTab(byStandardIndex)
    // Scroll to the framework section after the tab renders
    requestAnimationFrame(() => {
      const el = document.getElementById(`framework-${framework.code}`)
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
  }, [])

  // Section 03 heatmap → Section 06 deep-dive handoff.
  //   col 0/1/2 (SHALL/SHOULD/MAY) → By workload sub-tab, scrolls to that
  //   workload's article and opens the matching severity bucket.
  //   col 3 (WAIVED) → Violations sub-tab, pre-filters by workload + status,
  //   and scrolls to the deep-dive section header.
  // Zero-value cells are non-interactive in EditorialHeatmap, so empty cells
  // never reach this handler.
  const handleHeatmapCellClick = useCallback(
    (_rowIdx: number, colIdx: number, row: { label: string }) => {
      const workload = row.label as M365Workload

      if (colIdx === 3) {
        const violationsIndex = internalTabs.findIndex(t => t.key === 'violations')
        setActiveInternalTab(violationsIndex)
        setSelectedWorkloadFilter(workload)
        setViolationsTableSignal(prev => ({ status: 'Waived', tick: prev.tick + 1 }))
        requestAnimationFrame(() => {
          const el = document.getElementById('deep-dive')
          el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        })
        return
      }

      const groupForCol: Record<number, 'critical' | 'high' | 'medium'> = {
        0: 'critical',
        1: 'high',
        2: 'medium',
      }
      const group = groupForCol[colIdx]
      if (!group) return
      const byWorkloadIndex = internalTabs.findIndex(t => t.key === 'by-workload')
      setActiveInternalTab(byWorkloadIndex)
      setOpenWorkloadGroup(prev => ({ workload, group, tick: prev.tick + 1 }))
      requestAnimationFrame(() => {
        const el = document.getElementById(`workload-${workload}`)
        el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    },
    [],
  )

  // Group violations by workload (for By Workload tab)
  const violationsByWorkload = useMemo(() => {
    const grouped: Record<M365Workload, ComplianceViolation[]> = {
      Identity: [],
      Exchange: [],
      Teams: [],
      SharePoint: [],
      Defender: [],
      Other: [],
    }
    violations.forEach((v) => {
      const workload = getWorkloadForProduct(v.product || v.category)
      grouped[workload].push(v)
    })
    return grouped
  }, [violations])

  // Pass-through of every policy status so ViolationsTable can drive its own
  // Failing/Passing/All toggle. Workload filter still applies.
  const filteredViolations = useMemo(() => {
    if (selectedWorkloadFilter === 'all') return violations
    return violations.filter((v) => {
      const workload = getWorkloadForProduct(v.product || v.category)
      return workload === selectedWorkloadFilter
    })
  }, [violations, selectedWorkloadFilter])

  // Get external frameworks (exclude Guardian internal framework), sorted by FRAMEWORK_ORDER
  const externalFrameworks = useMemo(() => {
    if (!dashboardData?.frameworks) {
      return []
    }

    return dashboardData.frameworks
      .filter((f) => !f.isGuardian)
      .sort((a, b) => {
        const orderA = FRAMEWORK_ORDER[a.code] ?? 99
        const orderB = FRAMEWORK_ORDER[b.code] ?? 99
        return orderA - orderB
      })
  }, [dashboardData?.frameworks])

  // Get counts for the violations tab badge
  const openViolations = violations.filter((v) => v.status === 'Open')

  // Render loading state
  if (isLoading && !summary) {
    return (
      <div className="space-y-6">
        {/* Header skeleton */}
        <div className="animate-pulse">
          <div className="h-8 bg-dark-700 rounded w-1/3 mb-2" />
          <div className="h-4 bg-dark-700 rounded w-2/3" />
        </div>

        {/* Content skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-pulse">
          <div className="lg:col-span-1 h-80 bg-dark-700 rounded-2xl" />
          <div className="lg:col-span-2 space-y-4">
            <div className="h-32 bg-dark-700 rounded-xl" />
            <div className="h-32 bg-dark-700 rounded-xl" />
            <div className="h-32 bg-dark-700 rounded-xl" />
          </div>
        </div>
      </div>
    )
  }

  // Render error state
  if (isError) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-bold text-slate-100">Best Practice Analyzer</h2>
              <BetaBadge />
            </div>
            <p className="mt-1 text-sm text-slate-400">
              M365 security configuration checks based on industry frameworks
            </p>
          </div>
        </div>

        {/* Error message */}
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-400 flex-shrink-0" />
            <div>
              <h3 className="text-lg font-medium text-red-400">Failed to Load Compliance Data</h3>
              <p className="mt-2 text-sm text-slate-400">
                There was an error loading compliance data. This may be because the OPA service is not available
                or the backend APIs have not been deployed yet.
              </p>
              <button
                onClick={() => { refetchAll(); refetchControlChanges() }}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-500 rounded-lg transition-colors"
              >
                <ArrowPathIcon className="h-4 w-4" />
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6" data-tone="bpa">
      {/* Editorial standfirst — composer-driven brief at the top of the page. */}
      <PillarBrief
        brief={brief}
        score={summary?.overallScore ?? null}
        components={[]}
        tone="bpa"
        isLoading={isLoading}
        scoreLabel="Best Practice Score"
      />

      {/* Action row — Configure + Re-evaluate. Editorial pill buttons. */}
      <div className="flex items-center justify-end gap-3">
        <button
          onClick={() => setIsConfigModalOpen(true)}
          className="inline-flex items-center gap-2 rounded-full border border-[color:var(--color-line,#E6E8EC)] bg-[color:var(--color-paper,#fff)] px-4 py-2 text-[11px] font-bold uppercase tracking-[0.16em] text-[color:var(--color-navy,#212938)] transition-colors hover:bg-[color:var(--color-cream,#FBF8F2)]"
        >
          <Cog6ToothIcon className="h-3.5 w-3.5" />
          Configure
        </button>
        <button
          onClick={handleRefresh}
          disabled={triggerEvaluationMutation.isPending}
          className="inline-flex items-center gap-2 rounded-full bg-[#212938] px-4 py-2 text-[11px] font-bold uppercase tracking-[0.16em] text-white transition-colors hover:bg-[#0F1722] disabled:cursor-not-allowed disabled:opacity-60"
        >
          <ArrowPathIcon className={`h-3.5 w-3.5 ${triggerEvaluationMutation.isPending ? 'animate-spin' : ''}`} />
          {triggerEvaluationMutation.isPending ? 'Evaluating…' : 'Re-evaluate'}
        </button>
      </div>

      {/* Hero Section - Best practice checks overview. Editorial paper-white
          card with a soft accent shield tile to match Sections 01-06 below. */}
      <div className="rounded-[4px] border border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper,#fff)] p-6">
        <div className="flex items-start gap-4">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-[6px] bg-[color:var(--tone-accent-soft,#E8F0F6)]">
            <ShieldCheckIcon className="h-5 w-5 text-[color:var(--tone-accent-deep,#4F86A8)]" />
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-[color:var(--color-faint,#9CA3AF)]">
              Best Practice Analyzer
            </div>
            <h3 className="mt-1 text-[16px] font-semibold leading-snug text-[color:var(--color-navy,#212938)]">
              Check your Microsoft 365 configuration against industry best practices.
            </h3>
            <p className="mt-2 text-[13px] italic leading-relaxed text-[color:var(--color-mute,#6B7280)]">
              Evaluate your tenant against checks derived from{' '}
              <span className="font-semibold not-italic text-[color:var(--color-navy,#212938)]">CISA SCuBA</span>,{' '}
              <span className="font-semibold not-italic text-[color:var(--color-navy,#212938)]">NIST SP 800-53</span>,{' '}
              <span className="font-semibold not-italic text-[color:var(--color-navy,#212938)]">Microsoft MCSB</span>, and{' '}
              <span className="font-semibold not-italic text-[color:var(--color-navy,#212938)]">EIDSCA</span>{' '}
              frameworks. Identify configuration gaps and track your security posture over time.
            </p>
            <p className="mt-2 text-[12px] italic text-[color:var(--color-faint,#9CA3AF)]">
              Note: These are built-in checks for common security configurations. For comprehensive compliance assessment, pair with Microsoft Purview Compliance Manager.
            </p>
          </div>
        </div>
      </div>

      {/* Last evaluated timestamp and collection health summary. Editorial
          caption strip — mute eyebrow + navy timestamp, plus a tinted status
          pill for collection health. */}
      {summary?.lastEvaluatedAt && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[12px]">
          <div className="inline-flex items-baseline gap-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-[color:var(--color-faint,#9CA3AF)]">
              Last evaluated
            </span>
            <span className="tabular-nums text-[color:var(--color-navy,#212938)]">
              {new Date(summary.lastEvaluatedAt).toLocaleString()}
            </span>
            {summary.evaluationDurationMs && (
              <span className="italic text-[color:var(--color-mute,#6B7280)]">
                ({(summary.evaluationDurationMs / 1000).toFixed(1)}s)
              </span>
            )}
          </div>
          {collectionHealth && collectionHealth.length > 0 && (() => {
            const unhealthyCount = collectionHealth.filter(
              h => (h.health.graphExpected && !h.health.graphCollected) ||
                   (h.health.powerShellExpected && !h.health.powerShellCollected)
            ).length
            if (unhealthyCount === 0) {
              return (
                <span className={`${EDITORIAL_PILL_BASE} bg-[#E2F1E8] text-[#1F6B43]`}>
                  <span className="h-1.5 w-1.5 rounded-full bg-[#4A9B6E]" />
                  All workload data collected
                </span>
              )
            }
            return (
              <span className={`${EDITORIAL_PILL_BASE} bg-[#FDF3DF] text-[#8C6B22]`}>
                <ExclamationTriangleIcon className="h-3 w-3" />
                {unhealthyCount} workload{unhealthyCount > 1 ? 's' : ''} with incomplete data
              </span>
            )
          })()}
        </div>
      )}

      {/* No data state */}
      {!summary && (
        <div className="bg-dark-800 rounded-2xl border border-dark-700 p-8 text-center">
          <div className="max-w-md mx-auto">
            <ChartBarIcon className="h-12 w-12 text-slate-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-200 mb-2">No Compliance Data Available</h3>
            <p className="text-sm text-slate-400 mb-6">
              Run a compliance evaluation to see how your tenant measures up against CISA SCuBA, NIST SP 800-53, MCSB, and EIDSCA security controls.
            </p>
            <button
              onClick={handleRefresh}
              disabled={triggerEvaluationMutation.isPending}
              className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium text-white bg-primary-600 hover:bg-primary-500 rounded-lg transition-colors disabled:opacity-50"
            >
              <ArrowPathIcon className={`h-4 w-4 ${triggerEvaluationMutation.isPending ? 'animate-spin' : ''}`} />
              {triggerEvaluationMutation.isPending ? 'Running Evaluation...' : 'Run Evaluation'}
            </button>
          </div>
        </div>
      )}

      {/* Main content - only show if we have data */}
      {summary && dashboardData && (
        <>
          {/* ============================================================== */}
          {/* Phase B §6.1 — Top six worth fixing first                       */}
          {/* ============================================================== */}
          <section className="mt-2">
            <SectionEyebrow text="Section · 01 · This week" />
            <SectionHeadline level="page">
              Top six worth <em>fixing first.</em>
            </SectionHeadline>
            <p className="mt-2 max-w-2xl text-[14px] italic text-[color:var(--color-mute,#6B7280)]">
              Curated SHALL violations across starred frameworks, ranked by impact.
            </p>

            {/* Framework filter chips. Pure client-side filter on action.id. */}
            {availableBpaFrameworkChips.length > 1 && (
              <div className="mt-5 flex flex-wrap items-center gap-2 text-[10px] font-bold uppercase tracking-[0.16em]">
                <span className="text-[color:var(--color-faint,#9CA3AF)]">Filter:</span>
                {availableBpaFrameworkChips.map(code => {
                  const isActive = bpaFrameworkFilter === code
                  const label = code === 'all' ? 'All frameworks' : code
                  return (
                    <button
                      key={code}
                      type="button"
                      onClick={() => setBpaFrameworkFilter(code)}
                      className={[
                        'rounded-full border px-3 py-1.5 transition-colors',
                        isActive
                          ? 'border-[#212938] bg-[#212938] text-white'
                          : 'border-[color:var(--color-line,#E6E8EC)] bg-[color:var(--color-paper,#fff)] text-[color:var(--color-navy,#212938)] hover:bg-[color:var(--color-cream,#FBF8F2)]',
                      ].join(' ')}
                    >
                      {label}
                    </button>
                  )
                })}
              </div>
            )}

            <div className="mt-6">
              <PriorityQueue
                actions={filteredBpaPriorityActions}
                columns={2}
                emptyState={
                  bpaFrameworkFilter === 'all'
                    ? 'No open SHALL violations in starred frameworks. Posture is settled.'
                    : `No open SHALL violations in ${bpaFrameworkFilter}. Switch filter to see other frameworks.`
                }
                onItemClick={(action) => {
                  const policyCode = action.id.startsWith('bpa:') ? action.id.slice(4) : null
                  if (!policyCode) return
                  const matchingViolation = violations.find((v) => v.policyCode === policyCode)
                  if (!matchingViolation) {
                    toast(`No matching violation for ${policyCode} — it may have been resolved.`)
                    return
                  }
                  const violationsTabIndex = internalTabs.findIndex((t) => t.key === 'violations')
                  setActiveInternalTab(violationsTabIndex)
                  setSelectedWorkloadFilter('all')
                  setFocusViolationCode(policyCode)
                }}
              />
            </div>
          </section>

          {/* ============================================================== */}
          {/* Phase B/C §6.2 — Framework cards with 14-day sparkline          */}
          {/* ============================================================== */}
          <section className="mt-12">
            <SectionEyebrow text="Section · 02 · Frameworks in score" />
            <SectionHeadline level="page">
              How <em>each framework</em> is moving.
            </SectionHeadline>
            <p className="mt-2 max-w-2xl text-[14px] italic text-[color:var(--color-mute,#6B7280)]">
              Enable a framework to include it in your Guardian Score. MCSB is always on.
            </p>

            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {frameworkCardData.map(({ framework, delta7d, sparkline }) => {
                // MCSB is always-included; treat as starred regardless of flag.
                const isStarred =
                  framework.includeInGuardianScore ||
                  (framework.code ?? '').toUpperCase() === 'MCSB'
                if (!isStarred) {
                  return (
                    <DisabledFrameworkCard
                      key={framework.standardId}
                      code={framework.code}
                      name={framework.name}
                      onEnable={() => handleEnableFramework(framework.standardId)}
                      isPending={enablingStandardId === framework.standardId}
                    />
                  )
                }
                return (
                  <FrameworkSparklineCard
                    key={framework.standardId}
                    framework={framework}
                    delta7d={delta7d}
                    sparkline={sparkline}
                    onToggleStar={() => handleToggleIncludeInScore(framework)}
                    onOpen={() => handleViewFrameworkDetails(framework)}
                  />
                )
              })}

              {/* Assigned but not yet evaluated — same disabled treatment. */}
              {unstarredAvailableFrameworks.map(av => (
                <DisabledFrameworkCard
                  key={av.standardId}
                  code={av.code}
                  name={av.name}
                  onEnable={() => handleEnableFramework(av.standardId)}
                  isPending={enablingStandardId === av.standardId}
                />
              ))}
            </div>
          </section>

          {/* ============================================================== */}
          {/* Phase C §6.3 — Workload heatmap                                 */}
          {/* ============================================================== */}
          {workloadHeatmapRows.length > 0 && (
            <section className="mt-12">
              <SectionEyebrow text="Section · 03 · Where the gaps sit" />
              <SectionHeadline level="page">
                Workloads, <em>by criticality.</em>
              </SectionHeadline>
              <p className="mt-2 max-w-2xl text-[14px] italic text-[color:var(--color-mute,#6B7280)]">
                Open violations grouped by workload and rule severity, scoped to frameworks in your Guardian Score.
              </p>
              <div className="mt-6">
                <EditorialHeatmap
                  rows={workloadHeatmapRows}
                  columns={['SHALL', 'SHOULD', 'MAY', 'WAIVED']}
                  onCellClick={handleHeatmapCellClick}
                />
              </div>
            </section>
          )}

          {/* ============================================================== */}
          {/* Phase B §6.4 — 30-day composite trend                           */}
          {/* ============================================================== */}
          {trend30dKpis && (
            <section className="mt-12">
              <SectionEyebrow text="Section · 04 · 30-day trend" />
              <SectionHeadline level="page">
                The composite, <em>over the past month.</em>
              </SectionHeadline>
              <p className="mt-2 max-w-2xl text-[14px] italic text-[color:var(--color-mute,#6B7280)]">
                Daily Guardian Best Practice score. Net change at the right edge.
              </p>
              <div className="mt-6 rounded-[4px] border border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper,#fff)] px-4 pb-2 pt-4">
                <CompositeTrendChart points={trend30dKpis.points} />
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                <TrendKpi label="Net 30d" value={formatSignedDelta(trend30dKpis.net)} tone={trend30dKpis.net >= 0 ? 'pos' : 'neg'} />
                <TrendKpi label="Best 30d" value={`${trend30dKpis.best}`} />
                <TrendKpi label="Worst 30d" value={`${trend30dKpis.worst}`} />
                <TrendKpi label="Avg 30d" value={`${trend30dKpis.avg}`} />
              </div>
            </section>
          )}

          {/* ============================================================== */}
          {/* §6.5 — Recent control moves feed                                */}
          {/* One row per policy that flipped Pass↔Fail or was waived since   */}
          {/* the prior eval. Server pre-computes the signed GS delta per     */}
          {/* row; consumers must not re-sort. Section renders unconditionally*/}
          {/* — error, empty, and populated states each get their own panel. */}
          {/* See .agents/Tasks/High Priority/per-control-change-feed.md.    */}
          {/* ============================================================== */}
          <section className="mt-12">
            <SectionEyebrow text="Section · 05 · Recent control moves" />
            <SectionHeadline level="page">
              What <em>moved the score</em> lately.
            </SectionHeadline>
            <p className="mt-2 max-w-2xl text-[14px] italic text-[color:var(--color-mute,#6B7280)]">
              Individual controls that flipped between evaluations, scored for impact.
            </p>
            {controlChangesError ? (
              <div className="mt-6 flex items-center justify-between gap-4 rounded-[4px] border border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper,#fff)] px-5 py-4">
                <p className="text-[14px] italic text-[color:var(--color-mute,#6B7280)]">
                  Recent control moves couldn&apos;t be loaded.
                </p>
                <button
                  onClick={() => refetchControlChanges()}
                  className="text-[12px] font-bold uppercase tracking-[0.16em] text-[color:var(--color-navy,#212938)] underline decoration-dotted underline-offset-4 hover:opacity-70"
                >
                  Try again
                </button>
              </div>
            ) : controlChanges && controlChanges.length === 0 ? (
              <div className="mt-6 rounded-[4px] border border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper,#fff)] px-5 py-4">
                <p className="text-[14px] italic text-[color:var(--color-mute,#6B7280)]">
                  Nothing&apos;s moved since the last evaluation. Change a policy in your tenant and re-run an evaluation to see flips appear here.
                </p>
              </div>
            ) : !controlChanges ? null : (
              <ol className="mt-6 divide-y divide-[color:var(--color-rule,#E6E1D5)] overflow-hidden rounded-[4px] border border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper,#fff)]">
                {(controlChanges ?? []).map((change) => {
                  const stamp = new Intl.DateTimeFormat(undefined, {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  }).format(new Date(change.detectedAt))
                  // Verb mapping per plan §10; see CONTROL_CHANGE_TRAILING_CLAUSE.
                  const trailingClause = CONTROL_CHANGE_TRAILING_CLAUSE[change.changeKind]
                  const actor = formatTriggeredBy(change.triggeredBy)
                  const attribution = actor
                    ? `By ${actor} · ${stamp}`
                    : `Detected by scheduled scan · ${stamp}`
                  const category = (change.category ?? '').toUpperCase() || '—'
                  const product = (change.product ?? '').toUpperCase() || '—'
                  // Right-column renderer: ScoreDeltaChip handles its own sign
                  // coloring + 0.05 noise threshold. `Waived` always reads as a
                  // muted pill (positive magnitude but neutral display per §4).
                  // `scoreDelta == null` happens for unweighted policies.
                  const rightSlot = renderControlChangeRightSlot(change)
                  return (
                    <li
                      key={change.id}
                      className="flex items-center justify-between gap-4 px-5 py-3"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-[color:var(--color-faint,#9CA3AF)]">
                          {category} · {product}
                        </div>
                        <div className="mt-0.5 text-[14px] text-[color:var(--color-navy,#212938)]">
                          {change.policyTitle}. {trailingClause}
                          {change.affectedResourceCount > 0 && (
                            <span className="text-[color:var(--color-mute,#6B7280)]">
                              {' '}({change.affectedResourceCount} resources)
                            </span>
                          )}
                        </div>
                        <div className="mt-0.5 text-[12px] italic text-[color:var(--color-mute,#6B7280)]">
                          {attribution}
                        </div>
                      </div>
                      {rightSlot}
                    </li>
                  )
                })}
              </ol>
            )}
          </section>

          {/* ============================================================== */}
          {/* §6.6 — Deep dive sub-views                                      */}
          {/* Single editorial section header + an editorial pill sub-nav    */}
          {/* (replacing the old dark Tab.List). Each panel below is a Tab   */}
          {/* with editorial card styling. The Overview panel was removed —  */}
          {/* its grid duplicated Section 02 and its trend chart duplicated  */}
          {/* Section 04. NIST disclaimer migrated under Section 02.         */}
          {/* ============================================================== */}
          <section id="deep-dive" className="mt-14 scroll-mt-[160px]">
            <SectionEyebrow text="Section · 06 · Deep dive" />
            <SectionHeadline level="page">
              By <em>standard, workload, violations, history.</em>
            </SectionHeadline>
            <p className="mt-2 max-w-2xl text-[14px] italic text-[color:var(--color-mute,#6B7280)]">
              The full inventory. Pick a lens and drill in.
            </p>

            <Tab.Group selectedIndex={activeInternalTab} onChange={setActiveInternalTab}>
              {/* Editorial pill sub-nav. Same chip pattern as the Section 01
                  framework filter — active = solid navy, inactive = white
                  with rule border. */}
              <Tab.List className="mt-5 flex flex-wrap items-center gap-2 text-[10px] font-bold uppercase tracking-[0.16em]">
                {internalTabs.map((tab) => (
                  <Tab
                    key={tab.key}
                    className={({ selected }) =>
                      [
                        'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 transition-colors focus:outline-none',
                        selected
                          ? 'border-[#212938] bg-[#212938] text-white'
                          : 'border-[color:var(--color-line,#E6E8EC)] bg-[color:var(--color-paper,#fff)] text-[color:var(--color-navy,#212938)] hover:bg-[color:var(--color-cream,#FBF8F2)]',
                      ].join(' ')
                    }
                  >
                    <tab.icon className="h-3.5 w-3.5" />
                    {tab.name}
                    {tab.key === 'violations' && openViolations.length > 0 && (
                      <span className="ml-1 rounded-full bg-[#A03434] px-1.5 py-0.5 text-[10px] font-bold tabular-nums text-white">
                        {openViolations.length}
                      </span>
                    )}
                  </Tab>
                ))}
              </Tab.List>

              <Tab.Panels className="mt-6">
                <Tab.Panel>
                  <div className="space-y-6">
                    {(() => {
                      const sorted = [...(summary?.frameworkScores || [])].sort((a, b) => {
                        const orderA = FRAMEWORK_ORDER[a.code] ?? 99
                        const orderB = FRAMEWORK_ORDER[b.code] ?? 99
                        return orderA - orderB
                      })

                      return sorted.map((framework) => {
                        // Unstarred frameworks render as a muted "Enable to view" row
                        // (same id so scroll-to-framework handoffs from Section 02
                        // still resolve). Details, violations list, and controls
                        // are all hidden until the user opts in.
                        const isStarred =
                          framework.includeInGuardianScore ||
                          (framework.code ?? '').toUpperCase() === 'MCSB'
                        if (!isStarred) {
                          return (
                            <article
                              key={framework.standardId}
                              id={`framework-${framework.code}`}
                              className="scroll-mt-[160px] overflow-hidden rounded-[4px] border border-[color:var(--color-line,#E6E8EC)] bg-[color:var(--color-cream,#FBF8F2)]/70"
                            >
                              <div className="flex flex-wrap items-center justify-between gap-4 px-6 py-5">
                                <div className="min-w-0">
                                  <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--color-faint,#9CA3AF)]">
                                    {framework.code}
                                  </div>
                                  <h4 className="mt-0.5 text-[16px] font-semibold text-[color:var(--color-mute,#6B7280)]">
                                    {framework.code === 'EIDSCA'
                                      ? 'Entra ID Security Config Analyzer (EIDSCA)'
                                      : framework.name}
                                  </h4>
                                  <p className="mt-1 text-[12px] italic text-[color:var(--color-mute,#6B7280)]">
                                    Not contributing to your Guardian Score. Enable to see details.
                                  </p>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleEnableFramework(framework.standardId)}
                                  disabled={enablingStandardId === framework.standardId}
                                  className="flex-shrink-0 rounded-[2px] border border-[color:var(--color-navy,#212938)] bg-[color:var(--color-navy,#212938)] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-[color:var(--color-paper,#fff)] transition-opacity hover:opacity-85 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-navy,#212938)] focus-visible:ring-offset-2 disabled:cursor-wait disabled:opacity-60"
                                >
                                  {enablingStandardId === framework.standardId ? 'Enabling…' : 'Enable'}
                                </button>
                              </div>
                            </article>
                          )
                        }

                        const frameworkViolations = violations.filter(
                          (v) => (v.frameworkReferences ?? []).some(ref => refMatchesFramework(ref, framework.code))
                        )
                        const blurb = FRAMEWORK_BLURBS[framework.code]
                        const viewMode = frameworkViewMode[framework.code] ?? 'issues'

                        return (
                          <article
                            key={framework.standardId}
                            id={`framework-${framework.code}`}
                            className="scroll-mt-[160px] overflow-hidden rounded-[4px] border border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper,#fff)]"
                          >
                            {/* Framework header */}
                            <header className="flex items-center justify-between gap-4 border-b border-[color:var(--color-rule,#E6E1D5)] px-6 py-4">
                              <div className="flex min-w-0 items-center gap-4">
                                <ComplianceRadialScore
                                  score={framework.score}
                                  label={framework.code}
                                  size="sm"
                                />
                                <div className="min-w-0">
                                  <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--color-faint,#9CA3AF)]">
                                    {framework.code}
                                  </div>
                                  <h4 className="mt-0.5 text-[16px] font-semibold text-[color:var(--color-navy,#212938)]">
                                    {framework.code === 'EIDSCA'
                                      ? 'Entra ID Security Config Analyzer (EIDSCA)'
                                      : framework.name}
                                  </h4>
                                  <div className="mt-1 flex items-center gap-3">
                                    <span className="whitespace-nowrap text-[12px] text-[color:var(--color-mute,#6B7280)]">
                                      {framework.passedControls}/{framework.totalControls} controls passing
                                    </span>
                                    {blurb && (
                                      <>
                                        <span className="text-[color:var(--color-rule,#E6E1D5)]">|</span>
                                        <span className="truncate text-[12px] italic text-[color:var(--color-mute,#6B7280)]">
                                          {blurb}
                                        </span>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="flex flex-shrink-0 items-center gap-4">
                                {/* View toggle — editorial mini pill row */}
                                <div className="flex rounded-full border border-[color:var(--color-line,#E6E8EC)] bg-[color:var(--color-paper,#fff)] p-0.5">
                                  <button
                                    onClick={() => setFrameworkViewMode(prev => ({ ...prev, [framework.code]: 'issues' }))}
                                    className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em] transition-colors ${
                                      viewMode === 'issues'
                                        ? 'bg-[#212938] text-white'
                                        : 'text-[color:var(--color-mute,#6B7280)] hover:text-[color:var(--color-navy,#212938)]'
                                    }`}
                                  >
                                    Issues
                                  </button>
                                  <button
                                    onClick={() => setFrameworkViewMode(prev => ({ ...prev, [framework.code]: 'controls' }))}
                                    className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em] transition-colors ${
                                      viewMode === 'controls'
                                        ? 'bg-[#212938] text-white'
                                        : 'text-[color:var(--color-mute,#6B7280)] hover:text-[color:var(--color-navy,#212938)]'
                                    }`}
                                  >
                                    All controls
                                  </button>
                                </div>
                                <div className="text-right">
                                  <div className="font-display text-[24px] font-extrabold leading-none tabular-nums text-[color:var(--color-navy,#212938)]">
                                    {Math.round(framework.score)}%
                                  </div>
                                  <div className="mt-1 text-[10px] uppercase tracking-[0.16em] text-[color:var(--color-faint,#9CA3AF)]">
                                    Importance · {Math.max(1, Math.min(10, Math.round(framework.weight / 10) || 5))}/10
                                  </div>
                                </div>
                              </div>
                            </header>

                            {/* NIST SP 800-53 specific disclaimer */}
                            {framework.code === 'NIST_800_53' && (
                              <div className="px-6 pt-4">
                                <NistDisclaimer />
                              </div>
                            )}

                            {/* Content: Issues view or All Controls view */}
                            {viewMode === 'controls' ? (
                              <div className="p-6">
                                <FrameworkControlsList
                                  policies={frameworkViolations}
                                  frameworkCode={framework.code}
                                  passedControls={framework.passedControls}
                                  totalControls={framework.totalControls}
                                  partialControls={framework.partialControls}
                                  onAddWaiver={handleAddWaiver} onEditWaiver={handleEditWaiver} onDeleteWaiver={handleDeleteWaiver}
                                />
                              </div>
                            ) : frameworkViolations.length > 0 ? (
                              <div className="p-6">
                                <h5 className="mb-4 text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--color-faint,#9CA3AF)]">
                                  {frameworkViolations.filter((v) => v.status === 'Open').length} open violations
                                </h5>
                                <GroupedPoliciesDisplay
                                  policies={frameworkViolations}
                                  onAddWaiver={handleAddWaiver} onEditWaiver={handleEditWaiver} onDeleteWaiver={handleDeleteWaiver}
                                  frameworkCode={framework.code}
                                />
                              </div>
                            ) : (
                              <div className="p-6 text-center text-[13px] italic text-[color:var(--color-mute,#6B7280)]">
                                No violations for this framework.
                              </div>
                            )}
                          </article>
                        )
                      })
                    })()}
                  </div>
                </Tab.Panel>

                <Tab.Panel>
                  <div className="space-y-6">
                    {/* Standalone health warnings for workloads with missing EXPECTED data */}
                    {collectionHealth?.map((wh) => {
                      const hasMissingGraph = wh.health.graphExpected && !wh.health.graphCollected
                      const hasMissingPs = wh.health.powerShellExpected && !wh.health.powerShellCollected
                      if (!hasMissingGraph && !hasMissingPs) return null

                      return (
                        <div
                          key={`health-${wh.workload}`}
                          className="flex items-start gap-3 rounded-[4px] border border-[#C9AD80] bg-[#FBF6EC] p-4"
                        >
                          <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 text-[#8A6E3C]" />
                          <div className="text-[13px] text-[color:var(--color-navy,#212938)]">
                            <span className="font-semibold">{wh.workload}:</span>{' '}
                            {hasMissingGraph && hasMissingPs
                              ? 'Neither Graph nor PowerShell data was collected during the last evaluation. Results may be incomplete.'
                              : hasMissingPs
                                ? 'PowerShell data was not available during the last evaluation. Some policy checks may be incomplete.'
                                : 'Graph data was not available during the last evaluation. Some policy checks may be incomplete.'}
                            {wh.health.errors.length > 0 && (
                              <span className="mt-1 block italic text-[color:var(--color-mute,#6B7280)]">
                                Errors: {wh.health.errors.join('; ')}
                              </span>
                            )}
                          </div>
                        </div>
                      )
                    })}

                    {M365_WORKLOADS.map((workload) => {
                      // Mirror Section 03's heatmap: only Open + Pass items
                      // count here. Waived/Resolved/DataUnavailable are managed
                      // via the Violations sub-tab so the SHALL/SHOULD/MAY
                      // bucket counts here line up with the heatmap cells above.
                      const actionableViolations = violationsByWorkload[workload].filter(
                        (v) => v.status === 'Open' || v.status === 'Pass',
                      )
                      const openCount = actionableViolations.filter((v) => v.status === 'Open').length
                      const display = WORKLOAD_DISPLAY[workload]
                      const workloadHealth = collectionHealth?.find(h => h.workload === workload)

                      // Skip workloads with nothing actionable to show
                      if (actionableViolations.length === 0) return null

                      const isTargeted = openWorkloadGroup.workload === workload
                      return (
                        <article
                          key={workload}
                          id={`workload-${workload}`}
                          className="scroll-mt-[160px] overflow-hidden rounded-[4px] border border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper,#fff)]"
                        >
                          {/* Workload header */}
                          <header className="flex items-center justify-between border-b border-[color:var(--color-rule,#E6E1D5)] px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div
                                className="flex h-10 w-10 items-center justify-center rounded-[6px]"
                                style={{ backgroundColor: `${display.color}1A` }}
                              >
                                <display.icon className="h-5 w-5" style={{ color: display.color }} />
                              </div>
                              <div>
                                <h4 className="flex items-center gap-2 text-[16px] font-semibold text-[color:var(--color-navy,#212938)]">
                                  {workload}
                                  {workloadHealth && (
                                    <HealthBadge health={workloadHealth} />
                                  )}
                                </h4>
                                <p className="text-[12px] text-[color:var(--color-mute,#6B7280)]">
                                  {openCount} open violation{openCount === 1 ? '' : 's'}
                                </p>
                              </div>
                            </div>
                          </header>

                          {/* Workload violations - grouped by criticality */}
                          <div className="p-6">
                            <GroupedPoliciesDisplay
                              key={isTargeted ? `force-${openWorkloadGroup.group}-${openWorkloadGroup.tick}` : 'default'}
                              policies={actionableViolations}
                              onAddWaiver={handleAddWaiver} onEditWaiver={handleEditWaiver} onDeleteWaiver={handleDeleteWaiver}
                              initialOpenKey={isTargeted ? openWorkloadGroup.group ?? undefined : undefined}
                            />
                          </div>
                        </article>
                      )
                    })}

                    {/* Empty state for workloads */}
                    {Object.values(violationsByWorkload).every(
                      (vs) => vs.filter((v) => v.status === 'Open' || v.status === 'Pass').length === 0,
                    ) && (
                      <div className="rounded-[4px] border border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper,#fff)] p-8 text-center text-[13px] italic text-[color:var(--color-mute,#6B7280)]">
                        No violations found. Your tenant is fully compliant.
                      </div>
                    )}
                  </div>
                </Tab.Panel>

                <Tab.Panel>
                  <div className="mb-6 flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-[color:var(--color-faint,#9CA3AF)]">
                      <FunnelIcon className="h-3.5 w-3.5" />
                      Filter
                    </div>
                    <div className="flex flex-wrap gap-2 text-[10px] font-bold uppercase tracking-[0.16em]">
                      <button
                        onClick={() => setSelectedWorkloadFilter('all')}
                        className={[
                          'rounded-full border px-3 py-1.5 transition-colors',
                          selectedWorkloadFilter === 'all'
                            ? 'border-[#212938] bg-[#212938] text-white'
                            : 'border-[color:var(--color-line,#E6E8EC)] bg-[color:var(--color-paper,#fff)] text-[color:var(--color-navy,#212938)] hover:bg-[color:var(--color-cream,#FBF8F2)]',
                        ].join(' ')}
                      >
                        All
                      </button>
                      {M365_WORKLOADS.filter((w) => w !== 'Other').map((workload) => {
                        const display = WORKLOAD_DISPLAY[workload]
                        const count = violationsByWorkload[workload].filter((v) => v.status === 'Open').length
                        const IconComponent = display.icon
                        const wHealth = collectionHealth?.find(h => h.workload === workload)
                        const hasIncompleteData = wHealth && (
                          (wHealth.health.graphExpected && !wHealth.health.graphCollected) ||
                          (wHealth.health.powerShellExpected && !wHealth.health.powerShellCollected)
                        )
                        const isActive = selectedWorkloadFilter === workload
                        return (
                          <button
                            key={workload}
                            onClick={() => setSelectedWorkloadFilter(workload)}
                            title={hasIncompleteData ? `${workload} data collection incomplete` : undefined}
                            className={[
                              'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 transition-colors',
                              isActive
                                ? 'border-[#212938] bg-[#212938] text-white'
                                : 'border-[color:var(--color-line,#E6E8EC)] bg-[color:var(--color-paper,#fff)] text-[color:var(--color-navy,#212938)] hover:bg-[color:var(--color-cream,#FBF8F2)]',
                            ].join(' ')}
                          >
                            <IconComponent className="h-3.5 w-3.5" style={{ color: isActive ? '#FFFFFF' : display.color }} />
                            {workload}
                            {count > 0 ? (
                              <span
                                className={[
                                  'ml-1 rounded-full px-1.5 py-0.5 text-[10px] tabular-nums',
                                  isActive
                                    ? 'bg-white/20 text-white'
                                    : 'bg-[#FBEAEA] text-[#A03434]',
                                ].join(' ')}
                              >
                                {count}
                              </span>
                            ) : hasIncompleteData ? (
                              <ExclamationTriangleIcon className="h-3 w-3 text-[#C9AD80]" />
                            ) : null}
                          </button>
                        )
                      })}
                    </div>
                  </div>

                  {/* Filtered violations table */}
                  <ViolationsTable
                    key={`status-${violationsTableSignal.status}-${violationsTableSignal.tick}`}
                    violations={filteredViolations}
                    onAddWaiver={handleAddWaiver}
                    onEditWaiver={handleEditWaiver}
                    onDeleteWaiver={handleDeleteWaiver}
                    focusPolicyCode={focusViolationCode}
                    onFocusConsumed={handleFocusConsumed}
                    defaultStatusFilter={violationsTableSignal.status}
                  />
                </Tab.Panel>

                {/* History — evaluation list only. The duplicate trend chart
                    that lived here was removed; Section 04 owns the trend. */}
                <Tab.Panel>
                  <article className="rounded-[4px] border border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper,#fff)] p-6">
                    <header className="mb-4">
                      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--color-faint,#9CA3AF)]">
                        Evaluation history
                      </div>
                      <h4 className="mt-1 text-[16px] font-semibold text-[color:var(--color-navy,#212938)]">
                        The last ten runs.
                      </h4>
                    </header>
                    {trends.length > 0 ? (
                      <ol className="divide-y divide-[color:var(--color-rule,#E6E1D5)]">
                        {trends.slice(0, 10).map((point, index) => (
                          <li
                            key={index}
                            className="flex items-center justify-between gap-4 py-3"
                          >
                            <div className="min-w-0">
                              <div className="text-[14px] text-[color:var(--color-navy,#212938)]">
                                {new Date(point.date).toLocaleDateString('en-US', {
                                  weekday: 'long',
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                })}
                              </div>
                              <div className="mt-0.5 truncate text-[11px] uppercase tracking-[0.12em] text-[color:var(--color-faint,#9CA3AF)]">
                                {point.frameworkScores.map((f) => `${f.code} ${f.score}%`).join('  ·  ')}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-display text-[22px] font-extrabold leading-none tabular-nums text-[color:var(--color-navy,#212938)]">
                                {Math.round(point.overallScore)}%
                              </div>
                              <div className="mt-1 text-[10px] uppercase tracking-[0.16em] text-[color:var(--color-faint,#9CA3AF)]">
                                Overall
                              </div>
                            </div>
                          </li>
                        ))}
                      </ol>
                    ) : (
                      <div className="py-8 text-center text-[13px] italic text-[color:var(--color-mute,#6B7280)]">
                        No evaluation history yet. Run an evaluation to start tracking the trend.
                      </div>
                    )}
                  </article>
                </Tab.Panel>
              </Tab.Panels>
            </Tab.Group>
          </section>
        </>
      )}

      {/* Modals */}
      <StandardsConfigModal
        isOpen={isConfigModalOpen}
        onClose={() => setIsConfigModalOpen(false)}
        assignments={assignments}
        onSave={handleSaveConfig}
        isSaving={bulkUpdateStandardsMutation.isPending}
        frameworks={externalFrameworks}
        onToggleFrameworkStar={handleToggleIncludeInScore}
      />

      <WaiverModal
        isOpen={isWaiverModalOpen}
        onClose={() => {
          setIsWaiverModalOpen(false)
          setSelectedViolation(null)
        }}
        violation={selectedViolation}
        onSubmit={handleSubmitWaiver}
        onUpdate={handleUpdateWaiver}
        existingWaiver={selectedViolation?.hasWaiver ? selectedViolation.waiver : undefined}
        isSubmitting={createWaiverMutation.isPending || updateWaiverMutation.isPending}
      />
    </div>
  )
})

export default BestPracticeAnalyzerTab
